import { withFieldHint } from "@/src/components/HighOrderComponents";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { FormControl, FormLabel, RadioGroup as MuiRadioGroup, Typography } from "@mui/material";
import { FieldValues, useController } from "react-hook-form";
import type { RadioGroupProps } from "../lib/type";
import { RadioButton } from "./RadioButton";
import {validationRules} from "@/src/shared/utils/validationRules";

export const RadioGroup = <T extends FieldValues>({
    options,
    control,
    name,
    className,
    label,
    required,
    disabled,
    deps,
    orientation = "vertical",
    onChange: onChangeController,
    ...rest
}: RadioGroupProps<T>) => {
    const {
        field: { onChange, value = "", name: fieldName, ref },
        fieldState: { error },
    } = useController({
        control,
        name,
        rules: validationRules.RadioButtons({ required, disabled, deps }),
    });

    return (
        <FormControl>
            <FormLabel id={`${name}-label`} required={required} hidden={!label}>{label}</FormLabel>
            <MuiRadioGroup
                {...rest}
                row={orientation === "horizontal"}
                className={cn("w-full", className)}
                ref={ref}
                onChange={(e, value) => {
                    onChange(value)
                    if (onChangeController) {
                        onChangeController(e, value)
                    }
                }}
                value={value}
                name={fieldName}
                aria-labelledby={`${name}-label`}
            >
                {options.map((item) => {
                    if (item.hintIcon) {
                        const RadioButtonWithHint = withFieldHint(RadioButton)
                        return (
                            <RadioButtonWithHint
                                {...item}
                                disabled={disabled}
                                checked={item.value === value}
                                key={item.key}
                            />
                        )
                    }
                    return (
                        <RadioButton
                            {...item}
                            disabled={disabled}
                            checked={item.value === value}
                            key={item.key}
                        />
                    )
                })}
            </MuiRadioGroup>
            {error && (
                <Typography className={"text-dark-red font-normal text-xs"} component={"p"}>
                    {error.message}
                </Typography>
            )}
        </FormControl>
    )
}
